<template lang="pug">
	#virtualEdgePage
		Navbar(tab="virtualedge" :siteId="navbarSiteId" :site="site")
		main(v-if="isAdmin")
			.container
				.app-page-breadcrumbs
					AppBreadcrumbs(label="Virtual Edge" :allow-all-sites="true" :site-id="breadcrumbSiteId")
				v-row.top-header
					v-col.site-info
						.site-title {{ projectName }} Virtual Edges
				v-row
					v-col
						v-data-table.elevation-8.haq-data-table(
							:headers="headers"
							:items="installations"
							:loading="loading"
							:search="search"
							item-key="id"
							:items-per-page="10"
						)
							template(v-slot:top)
								v-row.align-center.virtual-edge-table-toolbar
									v-col(sm="5")
										HaqTextField(
											append-icon="mdi-magnify"
											v-model="search"
											placeholder="Search Virtual Edges"
											hide-details
										)
									v-col(sm="7").d-flex.justify-end
										v-btn.haq-btn.haq-btn-primary(@click="openCreateDialog")
											v-icon(left) mdi-plus
											| Add Virtual Edge
							template(v-slot:item.lastSeenAt="{ item }")
								v-tooltip(top)
									template(v-slot:activator="{ on, attrs }")
										span(v-bind="attrs" v-on="on") {{ formatRelative(item.lastSeenAt) }}
									span {{ formatAbsolute(item.lastSeenAt) }}
							template(v-slot:item.status="{ item }")
								span.status-pill(:class="`status-${item.status || 'unknown'}`") {{ item.status || '—' }}
							template(v-slot:item.actions="{ item }")
								v-btn.haq-btn.haq-btn-primary(small @click="openDevices(item)") Devices
		v-dialog(v-model="createDialog" content-class="haq-dialog" max-width="520" persistent)
			v-card.haq-dialog-card
				v-card-title Create Virtual Edge
				v-card-text(v-if="!createdApiKey")
					HaqTextField(
						v-model="newName"
						label="Name"
						:disabled="creating"
						@keyup.enter="createInstallation"
					)
				v-card-text(v-else)
					p.api-key-warning Copy this API key now. It will not be shown again.
					HaqTextField(
						:value="createdApiKey"
						label="API Key"
						readonly
						append-icon="mdi-content-copy"
						@click:append="copyApiKey"
					)
				v-card-actions
					v-spacer
					template(v-if="!createdApiKey")
						v-btn.haq-btn.haq-btn-secondary(@click="closeCreateDialog" :disabled="creating") Cancel
						v-btn.haq-btn.haq-btn-primary(:loading="creating" @click="createInstallation") Create
					template(v-else)
						v-btn.haq-btn.haq-btn-primary(@click="closeCreateDialog") Done
		v-dialog(
			v-model="devicesDialog"
			content-class="haq-dialog"
			max-width="900"
			:persistent="addDeviceDialog"
			@click:outside="onDevicesOutsideClick"
		)
			v-card.haq-dialog-card(v-if="selectedInstallation")
				v-card-title.haq-dialog-card-header
					span {{ selectedInstallation.name || 'Virtual Edge' }} Devices
					v-spacer
					v-icon.close-icon(text @click="closeDevicesDialog") mdi-close
				v-card-text
					v-data-table.haq-data-table.haq-data-table--embedded(
						:headers="deviceHeaders"
						:items="devices"
						:loading="devicesLoading"
						:search="deviceSearch"
						item-key="id"
						:items-per-page="10"
					)
						template(v-slot:top)
							v-row.align-center.virtual-edge-table-toolbar
								v-col(sm="6")
									HaqTextField(
										append-icon="mdi-magnify"
										v-model="deviceSearch"
										placeholder="Search Devices"
										hide-details
									)
								v-col(sm="6").d-flex.justify-end
									v-btn.haq-btn.haq-btn-primary(@click="openAddDeviceDialog")
										v-icon(left) mdi-plus
										| Add Device
						template(v-slot:item.tags="{ item }")
							span.device-tags(v-if="deviceTags(item).length")
								span.device-tag(v-for="tag in deviceTags(item)" :key="tag") {{ tag }}
							span(v-else) —
						template(v-slot:item.lastSeenAt="{ item }")
							v-tooltip(top)
								template(v-slot:activator="{ on, attrs }")
									span(v-bind="attrs" v-on="on") {{ formatRelative(item.lastSeenAt) }}
								span {{ formatAbsolute(item.lastSeenAt) }}
						template(v-slot:item.status="{ item }")
							span.status-pill(:class="`status-${item.status || 'unknown'}`") {{ item.status || '—' }}
						template(v-slot:item.actions="{ item }")
							.device-actions
								span(:title="isEditableDevice(item) ? undefined : 'Coming soon'")
									v-btn.haq-btn.haq-btn-secondary(
										small
										:disabled="!isEditableDevice(item)"
										@click="openEditDeviceDialog(item)"
									) Edit
								span(:title="configureDisabledReason(item)")
									v-btn.haq-btn.haq-btn-primary(
										small
										:disabled="!isConfigurableDevice(item)"
										@click="openDeviceConfigure(item)"
									) Configure
				v-card-actions
					v-spacer
					v-btn.haq-btn.haq-btn-secondary(@click="closeDevicesDialog") Close
		v-dialog(
			v-model="addDeviceDialog"
			content-class="haq-dialog"
			max-width="720"
			persistent
			scrollable
		)
			v-card.haq-dialog-card
				v-card-title.haq-dialog-card-header
					span {{ editingDeviceId ? 'Edit Device' : 'Add Device' }}
					v-spacer
					v-icon.close-icon(text @click="closeAddDeviceDialog" :disabled="addingDevice || testingDevice") mdi-close
				v-card-text
					v-row(dense)
						v-col(cols="12" sm="6")
							HaqTextField(v-model="addDeviceForm.name" label="Name" :disabled="addingDevice || testingDevice")
						v-col(cols="12" sm="6")
							HaqTextField(v-model="addDeviceForm.description" label="Description" :disabled="addingDevice || testingDevice")
						v-col(cols="12")
							v-switch.mt-0(
								v-model="addDeviceForm.isHalo"
								label="HALO device"
								color="primary"
								dense
								hide-details
								:disabled="addingDevice || testingDevice"
							)
							p.hc-add-device-hint Adds a Halo tag and enables Configure for the HALO Device Console.
						v-col(cols="12" sm="6")
							HaqTextField(
								v-model="addDeviceForm.host"
								label="Host"
								placeholder="hostname or IP"
								:disabled="addingDevice || testingDevice"
								@blur="onAddDeviceHostBlur"
							)
						v-col(cols="12" sm="6")
							HaqSelect(
								v-model="addDeviceForm.protocol"
								label="Protocol"
								:items="httpProtocolOptions"
								:disabled="addingDevice || testingDevice"
								@change="onAddDeviceProtocolChange"
							)
						v-col(cols="12" sm="6")
							HaqTextField(v-model="addDeviceForm.port" label="Port" type="number" :disabled="addingDevice || testingDevice")
						v-col(cols="12" sm="6")
							HaqTextField(v-model="addDeviceForm.path" label="Base Path" :disabled="addingDevice || testingDevice")
						v-col(cols="12" sm="6")
							HaqTextField(v-model="addDeviceForm.timeoutMs" label="Timeout (ms)" type="number" :disabled="addingDevice || testingDevice")
						v-col(cols="12" sm="6")
							HaqTextField(
								v-model="addDeviceForm.localSourceIp"
								label="Interface Bind"
								placeholder="default"
								:disabled="addingDevice || testingDevice"
							)
					.haq-section-title.mt-4 Advanced
					v-row(dense)
						v-col(cols="12" sm="6")
							HaqSelect(
								v-model="addDeviceForm.method"
								label="Method"
								:items="httpMethodOptions"
								:disabled="addingDevice || testingDevice"
							)
						v-col(cols="12" sm="6")
							HaqTextField(v-model="addDeviceForm.authUsername" label="Username" autocomplete="off" :disabled="addingDevice || testingDevice")
						v-col(cols="12" sm="6")
							HaqTextField(
								v-model="addDeviceForm.authPassword"
								:label="editingDeviceId ? 'Password (leave blank to keep)' : 'Password'"
								type="password"
								autocomplete="new-password"
								:disabled="addingDevice || testingDevice"
							)
						v-col(cols="12")
							HaqTextarea(
								v-model="addDeviceForm.headersText"
								label="Headers (JSON)"
								rows="3"
								:disabled="addingDevice || testingDevice"
							)
						v-col(cols="12" sm="4")
							v-switch.mt-0(
								v-model="addDeviceForm.enabled"
								label="Enabled"
								color="primary"
								dense
								hide-details
								:disabled="addingDevice || testingDevice"
							)
						v-col(cols="12" sm="4" v-if="addDeviceForm.protocol === 'https'")
							v-switch.mt-0(
								v-model="addDeviceForm.ignoreTls"
								label="Ignore TLS"
								color="primary"
								dense
								hide-details
								:disabled="addingDevice || testingDevice"
							)
						v-col(cols="12" sm="4")
							v-switch.mt-0(
								v-model="addDeviceForm.probeEnabled"
								label="Auto Probe"
								color="primary"
								dense
								hide-details
								:disabled="addingDevice || testingDevice"
							)
					.add-device-test-result(
						v-if="addDeviceTestResult"
						ref="addDeviceTestResult"
						:class="addDeviceTestResult.ok ? 'ok' : 'fail'"
					)
						.add-device-test-result-head
							strong
								| {{ addDeviceTestResult.ok ? 'OK' : 'Fail' }}
								template(v-if="addDeviceTestResult.status != null")  {{ addDeviceTestResult.status }}
								template(v-if="addDeviceTestResult.latencyMs != null")  · {{ addDeviceTestResult.latencyMs }}ms
							span.mono.muted(v-if="addDeviceTestResult.url") {{ addDeviceTestResult.url }}
						pre.add-device-test-result-body(v-if="addDeviceTestResult.error") {{ addDeviceTestResult.error }}
						pre.add-device-test-result-body(
							v-else-if="addDeviceTestResult.body != null && addDeviceTestResult.body !== ''"
						) {{ addDeviceTestResult.body }}
						p.muted.mb-0.mt-2(v-else) Empty body
				v-card-actions
					v-spacer
					v-btn.haq-btn.haq-btn-secondary(
						@click="closeAddDeviceDialog"
						:disabled="addingDevice || testingDevice"
					) Cancel
					v-btn.haq-btn.haq-btn-secondary(
						:loading="testingDevice"
						:disabled="addingDevice || !addDeviceForm.host"
						@click="testAddDevice"
					) Test
					v-btn.haq-btn.haq-btn-primary(
						:loading="addingDevice"
						:disabled="testingDevice"
						@click="saveEdgeDevice"
					) {{ editingDeviceId ? 'Save changes' : 'Save' }}
		HaloConsole(
			v-model="haloConsoleOpen"
			:edge-device="selectedDevice"
			:installation="selectedInstallation"
			:fleet-devices="devices"
			@halo-attrs="onHaloAttrs"
		)
</template>

<script>
import Navbar from '@/components/Navbar.vue';
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue';
import HaqTextField from '@/components/form/HaqTextField.vue';
import HaqSelect from '@/components/form/HaqSelect.vue';
import HaqTextarea from '@/components/form/HaqTextarea.vue';
import HaloConsole from '@/components/haloConsole/HaloConsole.vue';
import { HTTPClient } from '../assets/js/http';
import { firstAccessibleSite, isAccessibleSiteId, sitesUserCanAccess } from '../assets/js/siteAccess';

let client = new HTTPClient();

/**
 * Empty HTTP device form matching virtual-bms HttpDevices defaults.
 * @returns {Object}
 */
function emptyAddDeviceForm() {
	return {
		id: null,
		name: '',
		description: '',
		protocol: 'http',
		host: '127.0.0.1',
		port: 80,
		path: '/health',
		method: 'GET',
		timeoutMs: 5000,
		localSourceIp: '',
		authUsername: '',
		authPassword: '',
		headersText: '{}',
		enabled: true,
		probeEnabled: false,
		ignoreTls: true,
		isHalo: false,
	};
}

export default {
	name: 'VirtualEdge',
	components: {
		Navbar,
		AppBreadcrumbs,
		HaqTextField,
		HaqSelect,
		HaqTextarea,
		HaloConsole,
	},
	data() {
		return {
			site: null,
			search: '',
			loading: false,
			installations: [],
			createDialog: false,
			creating: false,
			newName: 'Virtual Edge',
			createdApiKey: null,
			selectedInstallation: null,
			selectedDevice: null,
			haloConsoleOpen: false,
			devicesDialog: false,
			devicesLoading: false,
			devices: [],
			deviceSearch: '',
			addDeviceDialog: false,
			addingDevice: false,
			testingDevice: false,
			addDeviceTestResult: null,
			editingDeviceId: null,
			addDeviceForm: emptyAddDeviceForm(),
			httpProtocolOptions: [
				{ text: 'HTTP', value: 'http' },
				{ text: 'HTTPS', value: 'https' },
			],
			httpMethodOptions: ['GET', 'POST', 'PUT', 'PATCH', 'HEAD'],
			headers: [
				{ text: 'Name', value: 'name' },
				{ text: 'Status', value: 'status' },
				{ text: 'UUID', value: 'uuid' },
				{ text: 'API Key Prefix', value: 'apiKeyPrefix' },
				{ text: 'Agent Version', value: 'agentVersion' },
				{ text: 'Config Rev', value: 'configRevision' },
				{ text: 'Last Seen', value: 'lastSeenAt' },
				{ text: 'Actions', value: 'actions', sortable: false },
			],
			deviceHeaders: [
				{ text: 'Name', value: 'name' },
				{ text: 'Protocol', value: 'protocol' },
				{ text: 'Tags', value: 'tags', sortable: false },
				{ text: 'Key', value: 'key' },
				{ text: 'Status', value: 'status' },
				{ text: 'Last Seen', value: 'lastSeenAt' },
				{ text: 'Actions', value: 'actions', sortable: false },
			],
		};
	},
	computed: {
		/**
		 * True when the current user is an admin or superAdmin.
		 * @returns {boolean}
		 */
		isAdmin() {
			const role = this.resolveUser()?.role;
			return role === 'admin' || role === 'superAdmin';
		},
		/**
		 * Site id for navbar context (first available site).
		 * @returns {string|null}
		 */
		navbarSiteId() {
			return this.$route.query.siteId || this.$route.params.siteId || this.site?.objectId || null;
		},
		/**
		 * Site id for the All Sites breadcrumb (query only; default is All Sites).
		 * @returns {string|null}
		 */
		breadcrumbSiteId() {
			return this.$route.query.siteId || null;
		},
		/**
		 * Current project object id from route or store.
		 * @returns {string|null}
		 */
		projectId() {
			return this.$route.params.projectId || this.$store.state.project?.objectId || null;
		},
		/**
		 * Display name for the current project.
		 * @returns {string}
		 */
		projectName() {
			const projectId = this.projectId;
			if (projectId && this.$store.state.projects?.length) {
				const current = this.$store.state.projects.find(p => p.objectId === projectId);
				if (current) return current.name;
			}
			return this.$store.state.project?.name || 'Project';
		},
	},
	methods: {
		/**
		 * Resolve the current user from the store or localStorage.
		 * @returns {Object|null}
		 */
		resolveUser() {
			let user = this.$store.state.user;
			if (user) return user;
			try {
				const raw = localStorage.getItem('user');
				if (!raw) return null;
				user = JSON.parse(raw);
				this.$store.commit('setUser', user);
				return user;
			} catch (error) {
				return null;
			}
		},
		/**
		 * Redirect non-admins away once the user record is known.
		 * @returns {boolean} true when the page may continue loading
		 */
		guardAdmin() {
			const user = this.resolveUser();
			if (!user) return false;
			if (user.role !== 'admin' && user.role !== 'superAdmin') {
				this.$router.replace('/');
				return false;
			}
			return true;
		},
		/**
		 * Load a site into navbar context (project-level page).
		 */
		async loadSiteContext() {
			try {
				let sites = this.$store.state.sites;
				if (!sites || sites.length === 0) {
					const res = await client.robustQuery('/api', { slim: true });
					const pid = this.projectId;
					sites = sitesUserCanAccess(res.sites || [], this.$store.state.user, pid);
					this.$store.commit('setSites', sites);
				}
				const leftover = this.$store.state.site;
				const accessible = this.$store.getters.accessibleSites || [];
				const leftoverId = leftover ? (leftover.objectId || leftover.id) : null;
				if (leftover && isAccessibleSiteId(leftoverId, accessible)) {
					this.site = leftover;
					return;
				}
				const first = this.$store.getters.firstAccessibleSite || firstAccessibleSite(sites, this.$store.state.user, this.projectId);
				if (first) {
					this.site = {
						objectId: first.objectId,
						name: first.name,
						commanderLicenseId: first.commanderLicenseId || null,
						project: first.project || null,
						canBeScheduled: first.canBeScheduled || false,
					};
				}
			} catch (error) {
				console.error('Failed to load site context', error);
			}
		},
		/**
		 * Fetch Virtual Edge installations for the current project.
		 */
		async loadInstallations() {
			if (!this.projectId || !this.isAdmin) return;
			this.loading = true;
			try {
				const res = await client.robustQuery('/api/edge/admin/installations', {
					projectId: this.projectId,
				});
				this.installations = (res.results || []).map(row => ({
					...row,
					uuid: row.uuid || '—',
					agentVersion: row.agentVersion || '—',
					apiKeyPrefix: row.apiKeyPrefix || '—',
				}));
			} catch (error) {
				console.error(error);
				window.M.toast({ html: 'Failed to load Virtual Edges' });
			} finally {
				this.loading = false;
			}
		},
		/**
		 * Open the create dialog.
		 */
		openCreateDialog() {
			this.newName = 'Virtual Edge';
			this.createdApiKey = null;
			this.createDialog = true;
		},
		/**
		 * Open the devices dialog for a Virtual Edge installation.
		 * @param {Object} item Edge installation row
		 */
		openDevices(item) {
			this.selectedInstallation = item || null;
			this.selectedDevice = null;
			this.devices = [];
			this.deviceSearch = '';
			this.addDeviceDialog = false;
			this.devicesDialog = true;
			this.loadDevices();
		},
		/**
		 * Close the devices dialog and clear selection.
		 */
		closeDevicesDialog() {
			if (this.addDeviceDialog) this.closeAddDeviceDialog();
			this.devicesDialog = false;
			this.devicesLoading = false;
			this.devices = [];
			this.deviceSearch = '';
			this.selectedInstallation = null;
			this.selectedDevice = null;
		},
		/**
		 * Ignore outside clicks on the devices dialog while Add Device is open.
		 */
		onDevicesOutsideClick() {
			if (this.addDeviceDialog) return;
			this.closeDevicesDialog();
		},
		/**
		 * Fetch EdgeDevices for the selected Virtual Edge installation.
		 */
		async loadDevices() {
			const installationId = this.selectedInstallation?.id;
			if (!installationId) return;
			this.devicesLoading = true;
			try {
				const res = await client.robustQuery(
					`/api/edge/admin/installations/${installationId}/devices`
				);
				this.devices = (res.results || []).map(row => ({
					...row,
					name: row.name || '—',
					protocol: row.protocol || '—',
					key: row.key || '—',
					status: row.status || null,
				}));
			} catch (error) {
				console.error(error);
				window.M.toast({ html: 'Failed to load edge devices' });
			} finally {
				this.devicesLoading = false;
			}
		},
		/**
		 * Tags shown on a device row.
		 * @param {Object} item
		 * @returns {string[]}
		 */
		deviceTags(item) {
			const tags = (item && item.tags)
				|| (item && item.attrs && item.attrs.tags)
				|| [];
			return Array.isArray(tags) ? tags.map(String).filter(Boolean) : [];
		},
		/**
		 * Whether this EdgeDevice is a tagged HALO HTTP device.
		 * @param {Object} item
		 * @returns {boolean}
		 */
		isHaloDevice(item) {
			if (!item) return false;
			const product = String(item.product || (item.attrs && item.attrs.product) || '').toLowerCase();
			if (product === 'halo') return true;
			if (item.attrs && item.attrs.isHalo === true) return true;
			return this.deviceTags(item).some((t) => String(t).toLowerCase() === 'halo');
		},
		/**
		 * Whether Configure is available (tagged Halo HTTP only).
		 * @param {Object} item Edge device row
		 * @returns {boolean}
		 */
		isConfigurableDevice(item) {
			const protocol = String(item && item.protocol || '').toLowerCase();
			if (protocol !== 'http' && protocol !== 'https') return false;
			return this.isHaloDevice(item);
		},
		/**
		 * Hover text when Configure is disabled.
		 * @param {Object} item
		 * @returns {string|undefined}
		 */
		configureDisabledReason(item) {
			if (this.isConfigurableDevice(item)) return undefined;
			const protocol = String(item && item.protocol || '').toLowerCase();
			if (protocol === 'http' || protocol === 'https') {
				return 'Coming soon — mark as HALO when adding the device';
			}
			return 'Coming soon';
		},
		/**
		 * Open the HALO Device Console for an edge device.
		 * @param {Object} item Edge device row
		 */
		openDeviceConfigure(item) {
			if (!this.isConfigurableDevice(item)) return;
			this.selectedDevice = item || null;
			this.haloConsoleOpen = true;
		},
		/**
		 * Keep fleet rows in sync when Halo console metadata changes.
		 * @param {{ deviceId: string, readOnlyMode: boolean }} payload
		 */
		onHaloAttrs(payload) {
			if (!payload || !payload.deviceId) return;
			const apply = (row) => {
				if (!row || (row.id !== payload.deviceId && row.key !== payload.deviceId)) return row;
				const attrs = row.attrs && typeof row.attrs === 'object' ? row.attrs : {};
				return {
					...row,
					readOnlyMode: payload.readOnlyMode === true,
					attrs: { ...attrs, readOnlyMode: payload.readOnlyMode === true },
				};
			};
			this.devices = (this.devices || []).map(apply);
			if (this.selectedDevice) this.selectedDevice = apply(this.selectedDevice);
		},
		/**
		 * Default port for an HTTP(S) protocol.
		 * @param {string} protocol
		 * @returns {number}
		 */
		defaultPortForProtocol(protocol) {
			return protocol === 'https' ? 443 : 80;
		},
		/**
		 * Parse a pasted host/URL into host and optional protocol, port, path, auth.
		 * @param {string} raw
		 * @returns {Object}
		 */
		parseHostInput(raw) {
			const trimmed = String(raw || '').trim();
			if (!trimmed) return { host: '' };
			const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed);
			try {
				const url = new URL(hasScheme ? trimmed : `http://${trimmed}`);
				const out = { host: url.hostname };
				if (hasScheme) {
					const proto = url.protocol.replace(':', '').toLowerCase();
					if (proto === 'http' || proto === 'https') {
						out.protocol = proto;
						out.port = url.port ? Number(url.port) : this.defaultPortForProtocol(proto);
					}
				} else if (url.port) {
					out.port = Number(url.port);
				}
				if (url.pathname && url.pathname !== '/') {
					out.path = url.pathname + (url.search || '');
				}
				if (url.username) {
					out.authUsername = decodeURIComponent(url.username);
					out.authPassword = decodeURIComponent(url.password || '');
				}
				return out;
			} catch (error) {
				let rest = trimmed.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, '');
				const slash = rest.search(/[/?#]/);
				if (slash >= 0) rest = rest.slice(0, slash);
				const host = rest.replace(/:\d+$/, '');
				return { host: host || trimmed };
			}
		},
		/**
		 * Whether Edit is available (HTTP devices only for now).
		 * @param {Object} item
		 * @returns {boolean}
		 */
		isEditableDevice(item) {
			const protocol = String(item && item.protocol || '').toLowerCase();
			return protocol === 'http' || protocol === 'https';
		},
		/**
		 * Open the Add Device modal (stacked over devices).
		 */
		openAddDeviceDialog() {
			this.editingDeviceId = null;
			this.addDeviceForm = emptyAddDeviceForm();
			this.addDeviceTestResult = null;
			this.testingDevice = false;
			this.addDeviceDialog = true;
		},
		/**
		 * Open the device form populated for an existing HTTP EdgeDevice.
		 * @param {Object} item
		 */
		async openEditDeviceDialog(item) {
			if (!this.isEditableDevice(item)) return;
			const installationId = this.selectedInstallation?.id;
			if (!installationId || !item?.id) return;
			this.editingDeviceId = item.id;
			this.addDeviceTestResult = null;
			this.testingDevice = false;
			this.addDeviceForm = emptyAddDeviceForm();
			this.addDeviceDialog = true;
			this.addingDevice = true;
			try {
				const res = await client.robustQuery(
					`/api/edge/admin/installations/${installationId}/devices/${item.id}`
				);
				const detail = res.results || {};
				const http = detail.httpDevice || {};
				const attrs = detail.attrs || {};
				const isHalo = http.isHalo === true
					|| String(http.product || attrs.product || '').toLowerCase() === 'halo'
					|| (Array.isArray(http.tags) && http.tags.some((t) => String(t).toLowerCase() === 'halo'))
					|| (Array.isArray(attrs.tags) && attrs.tags.some((t) => String(t).toLowerCase() === 'halo'));
				let headersText = '{}';
				try {
					headersText = JSON.stringify(http.headers && typeof http.headers === 'object' ? http.headers : {}, null, 2);
				} catch (e) {
					headersText = '{}';
				}
				this.addDeviceForm = {
					id: http.id || detail.key || item.key,
					name: http.name || detail.name || item.name || '',
					description: http.description != null ? http.description : (attrs.description || ''),
					protocol: http.protocol || attrs.protocol || 'http',
					host: http.host || attrs.host || '',
					port: http.port != null ? http.port : (attrs.port != null ? attrs.port : 80),
					path: http.path != null ? http.path : (attrs.path || '/health'),
					method: http.method || 'GET',
					timeoutMs: http.timeoutMs != null ? http.timeoutMs : 5000,
					localSourceIp: http.localSourceIp || '',
					authUsername: http.authUsername || '',
					authPassword: '',
					headersText,
					enabled: http.enabled !== false,
					probeEnabled: http.probeEnabled === true,
					ignoreTls: http.ignoreTls !== false,
					isHalo,
				};
			} catch (error) {
				console.error(error);
				window.M.toast({ html: error.message || 'Failed to load device' });
				this.closeAddDeviceDialog();
			} finally {
				this.addingDevice = false;
			}
		},
		/**
		 * Close the Add Device modal.
		 */
		closeAddDeviceDialog() {
			this.addDeviceDialog = false;
			this.addingDevice = false;
			this.testingDevice = false;
			this.addDeviceTestResult = null;
			this.editingDeviceId = null;
			this.addDeviceForm = emptyAddDeviceForm();
		},
		/**
		 * Build HTTP device payload from the add-device form (virtual-bms shape).
		 * @returns {Object|null}
		 */
		buildAddDevicePayload() {
			const form = this.addDeviceForm;
			const name = String(form.name || '').trim();
			const host = String(form.host || '').trim();
			if (!host) {
				window.M.toast({ html: 'Host is required' });
				return null;
			}
			let headers = {};
			try {
				const parsed = JSON.parse(form.headersText || '{}');
				if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
					throw new Error('Headers must be a JSON object');
				}
				headers = parsed;
			} catch (error) {
				window.M.toast({ html: error.message || 'Invalid headers JSON' });
				return null;
			}
			const payload = {
				name,
				description: form.description,
				protocol: form.protocol,
				host,
				port: Number(form.port),
				path: form.path,
				method: form.method,
				timeoutMs: Number(form.timeoutMs),
				localSourceIp: form.localSourceIp || null,
				authUsername: form.authUsername,
				authPassword: form.authPassword,
				headers,
				enabled: form.enabled !== false,
				probeEnabled: form.probeEnabled === true,
				ignoreTls: form.ignoreTls !== false,
				isHalo: form.isHalo === true,
				product: form.isHalo ? 'halo' : null,
				tags: form.isHalo ? ['Halo'] : [],
			};
			if (form.id) payload.id = form.id;
			return payload;
		},
		/**
		 * Poll an admin EdgeJob until completed/failed/cancelled or timeout.
		 * @param {string} installationId
		 * @param {string} jobId
		 * @param {number} timeoutMs
		 * @returns {Promise<Object>}
		 */
		async pollEdgeJob(installationId, jobId, timeoutMs) {
			const deadline = Date.now() + Math.max(15000, timeoutMs + 10000);
			while (Date.now() < deadline) {
				await new Promise(resolve => setTimeout(resolve, 1000));
				const res = await client.robustQuery(
					`/api/edge/admin/installations/${installationId}/jobs/${jobId}`
				);
				const job = res.results || {};
				const status = job.status;
				if (status === 'completed' || status === 'failed' || status === 'cancelled') {
					return job;
				}
			}
			throw new Error('Probe timed out waiting for the Virtual Edge agent');
		},
		/**
		 * Enqueue http_probe (like local edge Test) and show the result.
		 */
		async testAddDevice() {
			const installationId = this.selectedInstallation?.id;
			if (!installationId) return;
			const device = this.buildAddDevicePayload();
			if (!device) return;
			this.testingDevice = true;
			this.addDeviceTestResult = null;
			try {
				const created = await client.robustPost(
					`/api/edge/admin/installations/${installationId}/jobs`,
					{
						type: 'http_probe',
						payload: {
							...device,
							id: 'http-test',
							includeBody: true,
							testOnly: true,
						},
					}
				);
				const jobId = (created.results && (created.results.jobId || created.results.id)) || null;
				if (!jobId) throw new Error('Probe job was not created');
				const job = await this.pollEdgeJob(installationId, jobId, Number(device.timeoutMs) || 5000);
				const data = (job.result && job.result.data) || {};
				const ok = !!(job.result && job.result.success && data.ok);
				this.addDeviceTestResult = {
					ok,
					status: data.status,
					latencyMs: data.latencyMs,
					url: data.url,
					error: data.error || (job.status === 'failed' ? 'Probe failed' : null),
					body: data.body != null ? data.body : null,
				};
				await this.scrollAddDeviceTestResultIntoView();
			} catch (error) {
				console.error(error);
				this.addDeviceTestResult = {
					ok: false,
					error: error.message || 'Test failed',
				};
				await this.scrollAddDeviceTestResultIntoView();
			} finally {
				this.testingDevice = false;
			}
		},
		/**
		 * Scroll the Add Device modal so the test result panel is visible.
		 */
		async scrollAddDeviceTestResultIntoView() {
			await this.$nextTick();
			const el = this.$refs.addDeviceTestResult;
			const target = el && (el.$el || el);
			if (target && typeof target.scrollIntoView === 'function') {
				target.scrollIntoView({ behavior: 'smooth', block: 'end' });
			}
		},

		/**
		 * Apply protocol change and default port (80 / 443).
		 * @param {string} protocol
		 */
		onAddDeviceProtocolChange(protocol) {
			const next = protocol || this.addDeviceForm.protocol;
			this.addDeviceForm.protocol = next;
			this.addDeviceForm.port = this.defaultPortForProtocol(next);
		},
		/**
		 * Normalize host on blur (strip protocol, port, path from pasted URLs).
		 */
		onAddDeviceHostBlur() {
			const parsed = this.parseHostInput(this.addDeviceForm.host);
			if (!parsed.host) return;
			this.addDeviceForm.host = parsed.host;
			if (parsed.protocol) this.addDeviceForm.protocol = parsed.protocol;
			if (parsed.port != null) this.addDeviceForm.port = parsed.port;
			if (parsed.path) this.addDeviceForm.path = parsed.path;
			if (parsed.authUsername != null) this.addDeviceForm.authUsername = parsed.authUsername;
			if (parsed.authPassword != null) this.addDeviceForm.authPassword = parsed.authPassword;
		},
		/**
		 * Create or update an HTTP edge device via admin API.
		 */
		async saveEdgeDevice() {
			const installationId = this.selectedInstallation?.id;
			if (!installationId) return;
			const device = this.buildAddDevicePayload();
			if (!device) return;
			if (!device.name) {
				window.M.toast({ html: 'Name is required' });
				return;
			}
			const editing = !!this.editingDeviceId;
			this.addingDevice = true;
			try {
				await client.robustPost(
					`/api/edge/admin/installations/${installationId}/devices`,
					device
				);
				window.M.toast({ html: editing ? 'Device updated' : 'Device added' });
				this.closeAddDeviceDialog();
				await this.loadDevices();
			} catch (error) {
				console.error(error);
				window.M.toast({ html: error.message || (editing ? 'Failed to update device' : 'Failed to add device') });
			} finally {
				this.addingDevice = false;
			}
		},
		/**
		 * Close the create dialog and refresh the list if a key was shown.
		 */
		closeCreateDialog() {
			const shouldReload = !!this.createdApiKey;
			this.createDialog = false;
			this.creating = false;
			this.createdApiKey = null;
			this.newName = 'Virtual Edge';
			if (shouldReload) this.loadInstallations();
		},
		/**
		 * Create a new EdgeInstallation for this project.
		 */
		async createInstallation() {
			const name = (this.newName || '').trim() || 'Virtual Edge';
			this.creating = true;
			try {
				const res = await client.robustPost('/api/edge/admin/installations', {
					projectId: this.projectId,
					name,
				});
				const created = res.results || {};
				this.createdApiKey = created.apiKey || null;
				if (!this.createdApiKey) {
					window.M.toast({ html: 'Virtual Edge created' });
					this.closeCreateDialog();
					this.loadInstallations();
				}
			} catch (error) {
				console.error(error);
				window.M.toast({ html: error.message || 'Failed to create Virtual Edge' });
			} finally {
				this.creating = false;
			}
		},
		/**
		 * Copy the one-time API key to the clipboard.
		 */
		async copyApiKey() {
			if (!this.createdApiKey) return;
			try {
				await navigator.clipboard.writeText(this.createdApiKey);
				window.M.toast({ html: 'API key copied' });
			} catch (error) {
				console.error(error);
				window.M.toast({ html: 'Could not copy API key' });
			}
		},
		/**
		 * Relative time for last seen column.
		 * @param {string|Date|null} timestamp
		 * @returns {string}
		 */
		formatRelative(timestamp) {
			if (!timestamp) return '—';
			const ms = Date.now() - new Date(timestamp).getTime();
			if (Number.isNaN(ms)) return '—';
			const sec = Math.max(0, Math.floor(ms / 1000));
			if (sec < 60) return `${sec}s ago`;
			const min = Math.floor(sec / 60);
			if (min < 60) return `${min}m ago`;
			const hr = Math.floor(min / 60);
			if (hr < 24) return `${hr}h ago`;
			const day = Math.floor(hr / 24);
			if (day < 30) return `${day}d ago`;
			const month = Math.floor(day / 30);
			if (month < 12) return `${month}mo ago`;
			return `${Math.floor(month / 12)}y ago`;
		},
		/**
		 * Absolute timestamp for last seen tooltip.
		 * @param {string|Date|null} timestamp
		 * @returns {string}
		 */
		formatAbsolute(timestamp) {
			if (!timestamp) return '—';
			const date = new Date(timestamp);
			if (Number.isNaN(date.getTime())) return '—';
			return date.toLocaleString();
		},
	},
	mounted() {
		if (!this.guardAdmin()) return;
		this.loadSiteContext();
		this.loadInstallations();
	},
	watch: {
		'$route.params.projectId'() {
			if (!this.guardAdmin()) return;
			this.loadInstallations();
		},
		/**
		 * Re-check admin access when the store user arrives after mount.
		 * @param {Object|null} user
		 */
		'$store.state.user'(user) {
			if (!user) return;
			if (!this.guardAdmin()) return;
			if (!this.site) this.loadSiteContext();
			if (!this.installations.length) this.loadInstallations();
		},
	},
};
</script>

<style lang="scss" scoped>
.top-header {
	margin-top: 16px;
	margin-bottom: 0;
	.site-info {
		.site-title {
			color: var(--app-primary-txt);
			font-size: 28px;
		}
	}
}

.status-pill {
	text-transform: capitalize;
}

.device-actions {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.device-tags {
	display: inline-flex;
	flex-wrap: wrap;
	gap: 4px;
}

.device-tag {
	display: inline-block;
	padding: 2px 8px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.02em;
	background: rgba(66, 133, 244, 0.16);
	color: var(--score-blue-color, #4285f4);
	border: 1px solid rgba(66, 133, 244, 0.35);
}

.hc-add-device-hint {
	margin: 6px 0 0;
	font-size: 12px;
	color: var(--app-tertiary-txt);
	opacity: 0.85;
}

.api-key-warning {
	margin-bottom: 12px;
	color: var(--app-primary-txt);
}

.virtual-edge-table-toolbar {
	margin-bottom: 16px;
}

.haq-section-title {
	color: var(--app-primary-txt);
	font-size: 16px;
	font-weight: 500;
	margin-bottom: 4px;
}

.add-device-test-result {
	margin-top: 16px;
	padding: 12px 14px;
	border-radius: 12px;
	background: var(--app-secondary-bg);
	border: 1px solid rgba(168, 174, 222, 0.35);
	&.ok {
		border-color: rgba(76, 175, 80, 0.55);
	}
	&.fail {
		border-color: rgba(229, 115, 115, 0.55);
	}
}

.add-device-test-result-head {
	display: flex;
	flex-direction: column;
	gap: 4px;
	strong {
		color: var(--app-primary-txt);
	}
}

.add-device-test-result-body {
	margin: 10px 0 0;
	max-height: 220px;
	overflow: auto;
	white-space: pre-wrap;
	word-break: break-word;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	font-size: 12px;
	color: var(--app-primary-txt);
}

.mono {
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	font-size: 12px;
}

.muted {
	color: var(--app-tertiary-txt);
	opacity: 0.8;
}
</style>
